/**
 * Row comments (CC_11 Block 3, D-71) — ONE flat thread per record, shown in the
 * row detail view. Not field-precise, no nesting. Edit/delete are author-only
 * (the backend enforces it; the UI offers the buttons only on own comments).
 */

import { useCallback, useEffect, useState } from "react";

import { ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";

import "./ryfe.scss";

import type { RowComment } from "../api";

export const RowComments = ({
  wsId,
  fileId,
  rowId,
}: {
  wsId: string;
  fileId: string;
  rowId: string;
}) => {
  const { user } = useRyfe();
  const [comments, setComments] = useState<RowComment[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await ryfeApi.listRowComments(wsId, fileId, rowId);
      setComments(res.comments);
    } catch {
      setComments([]);
    }
  }, [wsId, fileId, rowId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const send = async () => {
    const body = draft.trim();
    if (!body || busy) {
      return;
    }
    setBusy(true);
    try {
      await ryfeApi.createRowComment(wsId, fileId, rowId, body);
      setDraft("");
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const edit = async (c: RowComment) => {
    const next = window.prompt("Kommentar bearbeiten", c.body);
    if (next === null || next.trim() === "" || next === c.body) {
      return;
    }
    await ryfeApi.updateRowComment(wsId, fileId, rowId, c.id, next.trim());
    await reload();
  };

  const remove = async (c: RowComment) => {
    await ryfeApi.deleteRowComment(wsId, fileId, rowId, c.id);
    await reload();
  };

  return (
    <div className="ryfe-comments" data-testid="row-comments">
      <span className="ryfe-elmenu__head">KOMMENTARE</span>
      <ul className="ryfe-comments__list">
        {comments.map((c) => (
          <li key={c.id} className="ryfe-comments__item">
            <div className="ryfe-comments__meta">
              <span className="ryfe-comments__author">
                {c.author_email || "Unbekannt"}
              </span>
              <span className="ryfe-muted">
                {new Date(c.created_at).toLocaleString()}
                {c.updated_at ? " · bearbeitet" : ""}
              </span>
              {user && c.user_id === user.id && (
                <>
                  <button
                    className="ryfe-btn ryfe-btn--ghost"
                    title="Bearbeiten"
                    onClick={() => void edit(c)}
                  >
                    ✎
                  </button>
                  <button
                    className="ryfe-btn ryfe-btn--ghost"
                    title="Löschen"
                    onClick={() => void remove(c)}
                  >
                    ×
                  </button>
                </>
              )}
            </div>
            <div className="ryfe-comments__body">{c.body}</div>
          </li>
        ))}
        {comments.length === 0 && (
          <li className="ryfe-muted">Noch keine Kommentare.</li>
        )}
      </ul>
      <div className="ryfe-comments__composer">
        <input
          className="ryfe-field-input"
          placeholder="Kommentar schreiben…"
          value={draft}
          data-testid="comment-draft"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              void send();
            }
          }}
        />
        <button
          className="ryfe-btn ryfe-btn--primary"
          disabled={!draft.trim() || busy}
          onClick={() => void send()}
        >
          Senden
        </button>
      </div>
    </div>
  );
};
